/**
 * Discover catalog scoping (DEV-1447).
 *
 * The catalog's answer set is decided HERE, in the query — never in the render.
 * Every exclusion below is a policy decision, not a styling choice, which is
 * why this lives in its own tested module rather than inline in the page.
 *
 * See STATUS_DESIGN_2026-07-12_DISCOVER_CATALOGS_PHASE1.md §7.4 / §7.5.
 */

import type { CommunityEntity } from '../../Components/CommunityCard/communityCardData';
import { isWorkshop } from '../../Data/Community/workshop';

/** Geo-level filter tiers. The grid's filter chips map to these. */
export type GeoTier = 'all' | 'country' | 'region' | 'local';

const REGION_TYPES = new Set([
  'geo_state',
  'geo_region',
  'geo_county',
  'geo_district',
  'geo_province',
]);

const LOCAL_TYPES = new Set([
  'geo_city',
  'geo_town',
  'geo_town_new_england_selectboard',
  'geo_neighborhood',
]);

/**
 * Which filter chip an entity belongs under.
 * `null` = no tier chip (geo_planet / geo_earth / geo_other) — still browsable
 * under "All", just not under a specific level.
 */
export function geoTier(entityType: string | undefined | null): Exclude<GeoTier, 'all'> | null {
  const t = (entityType || '').toLowerCase();
  if (t === 'geo_country') return 'country';
  if (REGION_TYPES.has(t)) return 'region';
  if (LOCAL_TYPES.has(t)) return 'local';
  return null;
}

/**
 * Is this community browsable in the Discover Communities catalog?
 *
 *   • geo-only            — §7.4. Organization communities are excluded; they
 *                           arrive with the Organizations catalog (the Enabler-
 *                           pillar surface). Accepted cost: org communities have
 *                           no browse surface in the interim (they stay reachable
 *                           via GlobalSearch and their parent geo).
 *   • platform_l4e        — the L4E sentinel (migration 225) is not a place.
 *                           Belt-and-braces: the geo_ prefix check already
 *                           excludes it, but naming it is cheaper than a future
 *                           reader wondering.
 *   • is_active === false — §7.5. An inactive geo is a location LABEL, not a
 *                           joinable community — there is nothing to follow, so
 *                           it is HIDDEN rather than disabled-and-explained.
 *                           IMPORTANT: this omission applies to the ANSWER SET,
 *                           not to the navigation path. When the geo-tree
 *                           drilldown is built, inactive geos must still render
 *                           as non-followable PATH SEGMENTS — otherwise a city
 *                           becomes unreachable through an inactive state.
 *   • visibility private  — DEV-890. Private communities never surface to
 *                           non-members. Enforced in the query, never the render.
 *   • workshops           — DEV-961 / ADR-006. Technically communities, but
 *                           functionally private to their owner and reached via
 *                           the user's profile, not by browsing.
 */
export function isCatalogEligible(c: CommunityEntity): boolean {
  const etype = (c.entity_type || '').toLowerCase();
  if (!etype.startsWith('geo_')) return false;
  if (etype === 'platform_l4e') return false;
  if (c.is_active === false) return false;
  if (c.visibility === 'private') return false;
  if (isWorkshop(c)) return false;              // DEV-961 / DEV-1483 — shared predicate
  return true;
}

/* ────────────────────────────────────────────────────────────────────────────
 * "Growing" — the recent-joins signal
 * ──────────────────────────────────────────────────────────────────────────── */

/** Trailing window for the growth signal. */
export const GROWTH_WINDOW_DAYS = 30;

export interface MembershipRow {
  community_entity_guid?: string;
  status?: string;
  /** DATE in Postgres; day granularity. Written once, on first join. */
  started_at?: string | null;
}

/**
 * Joins per community inside the trailing window.
 *
 * WHY A WINDOWED COUNT AND NOT `MAX(started_at)` — this is the whole design of
 * the shelf, and it is not obvious:
 *
 *   A brand-new community's founding memberships all land at (or near) its
 *   creation date. Rank by MOST RECENT join and the top of the list is simply
 *   the newest communities — i.e. the shelf silently becomes a second copy of
 *   "Newly added", which is the exact failure the shelf was added to avoid.
 *
 *   Counting joins in a trailing window asks a different question — "where are
 *   people arriving *now*?" — which an established community can win. That is a
 *   genuinely different answer set.
 *
 * Honesty note: this measures GROWTH, not activity. The shelf is labelled
 * accordingly ("Growing"), because there is no timestamped, geo-attributable
 * activity signal on a community today. `activity_events` (migration 241) is
 * the purpose-built table and is effectively empty — one emit hook exists
 * (chronicle publish). When its Tier-1 emit hooks land, THAT becomes the real
 * "Recently active" signal and this helper should be revisited.
 *
 * Zero backend: `started_at` is already on the hydrated membership row, and
 * `community_entity_guid` IS the geo guid — no walk, no org bleed.
 */
export function joinsInWindow(
  memberships: MembershipRow[] | undefined,
  now: Date = new Date(),
  windowDays: number = GROWTH_WINDOW_DAYS,
): Map<string, number> {
  const cutoff = new Date(now.getTime() - windowDays * 24 * 60 * 60 * 1000);
  const counts = new Map<string, number>();

  for (const m of memberships || []) {
    if (String(m.status || 'active').toLowerCase() !== 'active') continue;
    if (!m.started_at) continue;

    const started = new Date(m.started_at);
    if (Number.isNaN(started.getTime())) continue;
    if (started < cutoff || started > now) continue;

    const guid = String(m.community_entity_guid || '').toUpperCase();
    if (!guid) continue;
    counts.set(guid, (counts.get(guid) || 0) + 1);
  }

  return counts;
}

/**
 * Is this community too young for the Growing shelf?
 *
 * A community created inside the window has ALL of its joins inside the window
 * by definition, so it would dominate a growth ranking purely for being new —
 * and it is already on "Newly added". Excluding it keeps the two shelves
 * answering different questions.
 */
export function isTooNewForGrowth(
  c: CommunityEntity,
  now: Date = new Date(),
  windowDays: number = GROWTH_WINDOW_DAYS,
): boolean {
  if (!c.created_at) return false; // unknown age — let it compete
  const created = new Date(c.created_at);
  if (Number.isNaN(created.getTime())) return false;
  const cutoff = new Date(now.getTime() - windowDays * 24 * 60 * 60 * 1000);
  return created >= cutoff;
}
